// Custom hook for form calculations - Backend-accurate (mirrors ms-service PriceCalculator)
import { useState, useEffect, useRef } from 'react';
import {
  calculateBackendPricing,
  getSavingsForChargingType,
  getTaxesFromSpace,
  normalizeChargingType,
} from '../utils/bookingCalculationHelper';

export const useBookingCalculations = (form, selectedChargingOption, spaceDetails) => {
  const [calculatedValues, setCalculatedValues] = useState({
    hostDiscountValue: 0,
    bookingAfterHostDiscount: 0,
    msDiscountValue: 0,
    bookingAfterAllDiscounts: 0,
    taxAmount: 0,
    finalBookingValue: 0,
    baseCommission: 0,
    paymentToHost: 0,
    ccChargeValue: 0,
    finalCommissionToMS: 0,
    totalPrice: 0,
    basePrice: 0,
    bookingTotal: 0,
    totalSavings: 0,
    priceAfterDiscount: 0,
    savingsBreakdown: [],
    // NEW: Price adjustment tracking
    isPriceAdjusted: false,
    originalCalculatedPrice: 0,
    priceAdjustmentAmount: 0,
    priceAdjustmentPercent: 0
  });
  const isSyncingRef = useRef(false);
  const manuallyEditedRef = useRef(false); // Track if user manually edited bookingValue
  const systemCalculatedPriceRef = useRef(0); // Store system-calculated price for comparison

  // Check if space is Meet category
  const isMeetCategory = () => {
    if (!spaceDetails?.events?.[0]?.eventType?.spaceCategory?.name) return false;
    const categoryName = spaceDetails.events[0].eventType.spaceCategory.name.toLowerCase();
    return categoryName === 'meet';
  };

  // Watch form values and recalculate - Enhanced to match ms-web logic
  const handleFormValuesChange = (changedValues) => {
    // Detect if user manually changed bookingValue
    if (changedValues && changedValues.bookingValue !== undefined) {
      manuallyEditedRef.current = true;
    }
    
    // Reset manual edit flag when changing space, charging type, duration, or guests
    if (changedValues && (changedValues.selectedChargingType !== undefined || 
        changedValues.numberOfDays !== undefined ||
        changedValues.numberOfWeeks !== undefined ||
        changedValues.numberOfMonths !== undefined ||
        changedValues.hourlyDuration !== undefined ||
        changedValues.numberOfGuests !== undefined ||
        changedValues.guestCount !== undefined)) {
      manuallyEditedRef.current = false;
      systemCalculatedPriceRef.current = 0; // Reset system price tracking
    }
    
    const values = form.getFieldsValue([
      'bookingValue',
      'numberOfGuests',
      'guestCount',
      'numberOfDays',
      'numberOfWeeks',
      'numberOfMonths',
      'hourlyDuration',
      'hostDiscountPercent',
      'msDiscountPercent',
      'taxAmount',
      'commissionPercent',
      'ccChargePercent',
      'selectedChargingType'
    ]);

    // Determine unit rate from selected charging option; fallback to bookingValue when absent
    const unitRate = (() => {
      if (selectedChargingOption) {
        return parseFloat(
          selectedChargingOption.hourlyRate ||
          selectedChargingOption.dailyRate ||
          selectedChargingOption.weeklyRate ||
          selectedChargingOption.monthlyRate ||
          selectedChargingOption.blockRate || 0
        ) || 0;
      }
      return parseFloat(values.bookingValue) || 0;
    })();

    const guestCount = parseFloat(values.guestCount ?? values.numberOfGuests) || 1;
    
    // Calculate duration based on charging type
    const chargingType = selectedChargingOption?.type || values.selectedChargingType || 'daily';
    let duration = 1;
    
    switch(chargingType) {
      case 'hourly':
        duration = parseFloat(values.hourlyDuration) || 1;
        break;
      case 'daily':
        duration = parseFloat(values.numberOfDays) || 1;
        break;
      case 'weekly':
        duration = parseFloat(values.numberOfWeeks) || 1;
        break;
      case 'monthly':
        duration = parseFloat(values.numberOfMonths) || 1;
        break;
      case 'block':
        duration = 1;
        break;
      default:
        duration = 1;
    }

    // Calculate booking total
    // For Meet category: unit rate × duration (guest count doesn't affect price)
    // For other categories: unit rate × guest count × duration
    const bookingTotal = isMeetCategory() 
      ? unitRate * duration 
      : unitRate * guestCount * duration;

    // Sync bookingValue (A) to reflect pre-discount total for financial step
    // Use ref to prevent the sync from triggering another calculation in the same cycle
    // Only auto-sync if user hasn't manually edited the field
    const currentBookingValue = parseFloat(values.bookingValue) || 0;
    if (selectedChargingOption && !isSyncingRef.current && !manuallyEditedRef.current && Math.abs(currentBookingValue - bookingTotal) > 0.009) {
      isSyncingRef.current = true;
      form.setFieldsValue({ bookingValue: Number(bookingTotal.toFixed(2)) });
      // Reset flag after a short delay to allow the form update to complete
      setTimeout(() => {
        isSyncingRef.current = false;
      }, 0);
    }

    // Use manually entered bookingValue if user has edited it, otherwise use calculated bookingTotal
    const finalBookingTotal = manuallyEditedRef.current ? currentBookingValue : bookingTotal;
    // Recalculate unitRate from manual booking value for accurate discount calculations
    // For Meet category: divide only by duration, not guest count
    const effectiveUnitRate = manuallyEditedRef.current 
      ? (isMeetCategory() ? (currentBookingValue / duration) : (currentBookingValue / (guestCount * duration)))
      : unitRate;

    // Build savings and taxes from spaceDetails if available, else fallback to form percents
    const normalizedCT = normalizeChargingType(chargingType);
    let savingsInfo = [];
    let taxes = [];
    if (spaceDetails) {
      savingsInfo = getSavingsForChargingType(spaceDetails, normalizedCT) || [];
      taxes = getTaxesFromSpace(spaceDetails) || [];
    }

    // Fallback: create simple percentage-based savings if none from space
    if ((!savingsInfo || savingsInfo.length === 0)) {
      const hostDiscountPercent = parseFloat(values.hostDiscountPercent) || 0;
      const msDiscountPercent = parseFloat(values.msDiscountPercent) || 0;
      if (hostDiscountPercent > 0) {
        savingsInfo.push({ terms: 1, amount: hostDiscountPercent, saving: { id: 'HOST', name: 'Host Discount', amountType: 'PERCENTAGE' } });
      }
      if (msDiscountPercent > 0) {
        savingsInfo.push({ terms: 1, amount: msDiscountPercent, saving: { id: 'MS', name: 'Deal', amountType: 'PERCENTAGE' } });
      }
    }

    // Fallback: single flat tax rate if no tax objects available
    if (!taxes || taxes.length === 0) {
      const taxPercent = parseFloat(values.taxValue) || 0;
      if (taxPercent > 0) {
        taxes = [{ id: 'TAX', taxName: 'Tax', taxAmount: taxPercent, taxOnTax: [] }];
      }
    }

    // Calculate backend-accurate pricing
    // For Meet category: use guestCount as 1 in pricing calculation (price is fixed regardless of guests)
    const pricing = calculateBackendPricing({
      unitRate: effectiveUnitRate,
      duration,
      guestCount: isMeetCategory() ? 1 : guestCount,
      chargingType: normalizedCT,
      packagePrice: 0,
      savingsInfo,
      taxes,
    });

    const totalSavings = pricing.totalSavings || 0;
    const priceAfterDiscount = pricing.priceAfterDiscount || 0;
    const taxAmount = pricing.totalTax || 0;
    
    // Override tax amount if manually entered
    const manualTaxAmount = parseFloat(values.taxAmount);
    const finalTaxAmount = !isNaN(manualTaxAmount) ? manualTaxAmount : taxAmount;
    
    const finalBookingValue = priceAfterDiscount + finalTaxAmount;
    
    // Calculate commission and payments
    const commissionPercent = parseFloat(values.commissionPercent) || 0;
    const baseCommission = priceAfterDiscount * (commissionPercent / 100);
    
    // Extract MS vs Host discount values from breakdown if possible
    const msEntry = (pricing.savingsBreakdown || []).find(s => (s.name || '').toLowerCase().includes('deal') || (s.name || '').toLowerCase().includes('ms'));
    const hostEntry = (pricing.savingsBreakdown || []).find(s => (s.name || '').toLowerCase().includes('host'));
    const msDiscountValue = msEntry?.value || 0;
    const hostDiscountValue = hostEntry?.value || 0;

    // Payment to host = final booking value + MS discount - commission (as per existing UI logic)
    const paymentToHost = finalBookingValue + msDiscountValue - baseCommission;
    
    // CC charge calculation
    const ccChargePercent = parseFloat(values.ccChargePercent) || 0;
    const ccChargeValue = finalBookingValue * (ccChargePercent / 100);
    
    // Final commission to MS = base commission - MS discount - CC charge
    const finalCommissionToMS = baseCommission - msDiscountValue - ccChargeValue;
    
    // Use backend-like savings breakdown for display
    const savingsBreakdown = pricing.savingsBreakdown || [];

    // Store system-calculated price for comparison (use bookingTotal, not finalBookingTotal)
    // bookingTotal is the actual system-calculated value before any manual adjustment
    systemCalculatedPriceRef.current = bookingTotal;
    
    // Detect price adjustment: compare manual bookingValue with system-calculated total
    let priceAdjustmentData = {
      isPriceAdjusted: false,
      originalCalculatedPrice: 0,
      priceAdjustmentAmount: 0,
      priceAdjustmentPercent: 0
    };
    
    if (manuallyEditedRef.current && systemCalculatedPriceRef.current > 0) {
      const manualPrice = parseFloat(values.bookingValue || currentBookingValue);
      const systemPrice = systemCalculatedPriceRef.current;
      
      // Consider adjusted if difference is more than 1 cent
      if (Math.abs(manualPrice - systemPrice) > 0.01) {
        const adjustmentAmount = manualPrice - systemPrice;
        const adjustmentPercent = (adjustmentAmount / systemPrice) * 100;
        
        priceAdjustmentData = {
          isPriceAdjusted: true,
          originalCalculatedPrice: parseFloat(systemPrice.toFixed(2)),
          priceAdjustmentAmount: parseFloat(adjustmentAmount.toFixed(2)),
          priceAdjustmentPercent: parseFloat(adjustmentPercent.toFixed(2))
        };
      }
    }

    setCalculatedValues({
      hostDiscountValue: hostDiscountValue.toFixed(2),
      bookingAfterHostDiscount: (finalBookingTotal - hostDiscountValue).toFixed(2),
      msDiscountValue: msDiscountValue.toFixed(2),
      bookingAfterAllDiscounts: priceAfterDiscount.toFixed(2),
      taxAmount: finalTaxAmount.toFixed(2),
      finalBookingValue: finalBookingValue.toFixed(2),
      baseCommission: baseCommission.toFixed(2),
      paymentToHost: paymentToHost.toFixed(2),
      ccChargeValue: ccChargeValue.toFixed(2),
      finalCommissionToMS: finalCommissionToMS.toFixed(2),
      totalPrice: finalBookingValue.toFixed(2),
      basePrice: effectiveUnitRate.toFixed(2),
      bookingTotal: finalBookingTotal.toFixed(2),
      totalSavings: totalSavings.toFixed(2),
      priceAfterDiscount: priceAfterDiscount.toFixed(2),
      savingsBreakdown: savingsBreakdown,
      duration: duration,
      guestCount: guestCount,
      // Include price adjustment tracking
      ...priceAdjustmentData
    });
  };

  // Ensure initial calculation runs on mount and when charging option changes
  useEffect(() => {
    // Delay initial calculation to ensure form fields are set
    const timer = setTimeout(() => {
      handleFormValuesChange();
    }, 100);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedChargingOption || spaceDetails) {
      // Delay calculation to ensure all form fields (including discounts) are set
      const timer = setTimeout(() => {
        handleFormValuesChange();
      }, 150);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChargingOption, spaceDetails]);

  return {
    calculatedValues,
    handleFormValuesChange
  };
};
